import { useEffect, useState } from 'react';
import { act } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { EmailView, FileView } from '../lib/types';
import { Drawer, Field, inputClass } from './admin/kit';
import { CannedPicker } from './Canned';
import { Attach } from './EmailPane';
import { Button } from './ui';

interface Sender {
  id: string;
  name: string;
  address: string;
}

const addresses = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * A new email to a customer, from one of the organization's mailboxes. Once
 * sent it opens in Work for its outcome; the customer's answer comes back as
 * new work in the same conversation.
 */
export function ComposeEmail({ onClose, to: initialTo = '' }: { onClose: () => void; to?: string }) {
  const [senders, setSenders] = useState<Sender[] | null>(null);
  const [f, setF] = useState({ mailboxId: '', to: initialTo, cc: '', subject: '', body: '' });
  const [files, setFiles] = useState<FileView[]>([]);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: string) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    api<Sender[]>('GET', '/emails/mailboxes')
      .then((s) => {
        setSenders(s);
        if (s[0]) setF((x) => ({ ...x, mailboxId: x.mailboxId || s[0].id }));
      })
      .catch(() => setSenders([]));
  }, []);
  const valid = (list: string[]) => list.every((a) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a));
  const to = addresses(f.to);
  const cc = addresses(f.cc);
  const ready = f.mailboxId && to.length > 0 && valid(to) && valid(cc) && f.subject.trim() && f.body.trim();

  async function send() {
    setBusy(true);
    const view = await act(
      () => api<EmailView>('POST', '/emails', { mailboxId: f.mailboxId, to, cc, subject: f.subject, body: f.body, attachmentIds: files.map((x) => x.id) }),
      'Email sent',
    );
    setBusy(false);
    if (!view) return;
    // Its outcome next, in Work
    const st = useNova.getState();
    st.set({ emails: { ...st.emails, [view.interactionId]: view }, selectedId: view.interactionId, view: 'cockpit' });
    onClose();
  }

  return (
    <Drawer
      title="New email"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !ready} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onClose}>
            Cancel
          </Button>
          <span className="ml-auto">
            <CannedPicker channel="email" customer={null} align="right" onPick={(t) => set('body', f.body.trim() ? `${f.body.trimEnd()}\n\n${t}` : t)} />
          </span>
        </>
      }
    >
      {senders && senders.length === 0 ? (
        <p className="text-sm text-ink-2">There is no mailbox to send from yet. An admin adds one under Admin &gt; Channels &gt; Email.</p>
      ) : (
        <>
          <Field label="From">
            {(id) => (
              <select id={id} className={inputClass} value={f.mailboxId} onChange={(e) => set('mailboxId', e.target.value)}>
                {(senders ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} &lt;{s.address}&gt;
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="To" hint="One or more addresses, separated by commas">
            {(id) => <input id={id} type="text" inputMode="email" className={inputClass} value={f.to} onChange={(e) => set('to', e.target.value)} placeholder="customer@example.com" />}
          </Field>
          <Field label="Cc">{(id) => <input id={id} type="text" inputMode="email" className={inputClass} value={f.cc} onChange={(e) => set('cc', e.target.value)} />}</Field>
          <Field label="Subject">{(id) => <input id={id} className={inputClass} value={f.subject} maxLength={300} onChange={(e) => set('subject', e.target.value)} />}</Field>
          <Field label="Message" hint="The mailbox's signature is added under it">
            {(id) => <textarea id={id} rows={10} className={inputClass} value={f.body} onChange={(e) => set('body', e.target.value)} />}
          </Field>
          <Attach files={files} onChange={setFiles} />
        </>
      )}
    </Drawer>
  );
}
